// CookingScreen.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

const RECIPES = [
  {
    id: 'banana-smoothie', title: '🥤 Banana Smoothie', time: '5 min', level: 'Easy',
    emoji: '🍌', color: '#FECA57',
    ingredients: ['1 ripe banana', '1 cup milk', '1 spoon of honey', '4-5 ice cubes'],
    steps: [
      { step: 1, icon: '🍌', text: 'Peel the banana and break it into pieces' },
      { step: 2, icon: '🥛', text: 'Pour the milk into the blender' },
      { step: 3, icon: '🍯', text: 'Add the honey and ice cubes' },
      { step: 4, icon: '🌀', text: 'Ask a grown-up to help blend for 30 seconds' },
      { step: 5, icon: '🥤', text: 'Pour into a glass and enjoy!' }
    ]
  },
  {
    id: 'pizza-toast', title: '🍕 Mini Pizza Toast', time: '10 min', level: 'Easy',
    emoji: '🍕', color: '#FF6B6B',
    ingredients: ['2 slices of bread', '4 spoons tomato sauce', 'Cheese', 'Your favorite toppings'],
    steps: [
      { step: 1, icon: '🍞', text: 'Place bread slices on a baking tray' },
      { step: 2, icon: '🍅', text: 'Spread tomato sauce on each slice' },
      { step: 3, icon: '🧀', text: 'Sprinkle lots of cheese on top' },
      { step: 4, icon: '🫑', text: 'Add your favorite toppings' },
      { step: 5, icon: '🔥', text: 'Ask a grown-up to toast for 5-7 minutes' }
    ]
  },
  {
    id: 'fruit-salad', title: '🍓 Rainbow Fruit Salad', time: '8 min', level: 'Easy',
    emoji: '🍓', color: '#26de81',
    ingredients: ['Strawberries', 'Blueberries', 'Banana', 'Orange', 'Grapes', 'Honey'],
    steps: [
      { step: 1, icon: '✋', text: 'Wash all the fruit carefully' },
      { step: 2, icon: '🍓', text: 'Slice the strawberries and banana' },
      { step: 3, icon: '🍊', text: 'Ask a grown-up to peel and cut the orange' },
      { step: 4, icon: '🫙', text: 'Put all fruit in a big bowl' },
      { step: 5, icon: '🍯', text: 'Drizzle honey on top and mix gently' }
    ]
  }
];

export default function CookingScreen() {
  const navigate = useNavigate();
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [done, setDone] = useState(false);

  if (selectedRecipe) {
    const recipe = RECIPES.find(r => r.id === selectedRecipe);
    const step = recipe.steps[currentStep];

    return (
      <div className="screen">
        <div className="screen-header" style={{ background: `linear-gradient(135deg, ${recipe.color}, ${recipe.color}99)`, paddingBottom: 16 }}>
          <button className="btn-back" onClick={() => { setSelectedRecipe(null); setCurrentStep(0); setDone(false); }} style={{ background: 'rgba(255,255,255,0.3)' }}>←</button>
          <h1 style={{ fontFamily: 'var(--font-title)', fontSize: '1.3rem', flex: 1 }}>{recipe.title}</h1>
        </div>
        <div className="screen-body" style={{ paddingTop: 20 }}>
          {!done ? (
            <>
              <div className="progress-bar" style={{ marginBottom: 20 }}>
                <div className="progress-bar-fill" style={{ width: `${((currentStep + 1) / recipe.steps.length) * 100}%`, background: recipe.color }} />
              </div>
              <div className="card" style={{ textAlign: 'center', padding: 30, marginBottom: 20 }}>
                <div style={{ fontSize: '4rem', marginBottom: 12 }}>{step.icon}</div>
                <div style={{ color: 'var(--gray)', fontSize: '0.85rem', marginBottom: 8 }}>Step {step.step} of {recipe.steps.length}</div>
                <p style={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.6 }}>{step.text}</p>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                {currentStep > 0 && (
                  <button className="btn" style={{ flex: 1, background: 'var(--bg)' }} onClick={() => setCurrentStep(s => s - 1)}>← Back</button>
                )}
                <button
                  className="btn btn-primary" style={{ flex: 2 }}
                  onClick={() => { if (currentStep < recipe.steps.length - 1) setCurrentStep(s => s + 1); else setDone(true); }}
                >
                  {currentStep < recipe.steps.length - 1 ? 'Next Step →' : '🎉 Finished!'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: '5rem', marginBottom: 12 }}>{recipe.emoji}</div>
              <h2 style={{ fontFamily: 'var(--font-title)', color: 'var(--purple)' }}>Amazing job, Chef!</h2>
              <p style={{ color: 'var(--gray)', margin: '12px 0 24px' }}>You made {recipe.title}! Enjoy your delicious creation! 😋</p>
              <button className="btn btn-primary btn-full" onClick={() => { setSelectedRecipe(null); setCurrentStep(0); setDone(false); }}>
                Cook Another Recipe 🍳
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="screen">
      <div className="screen-header" style={{ background: 'linear-gradient(135deg, #A55EEA, #8854D0)', paddingBottom: 16 }}>
        <button className="btn-back" onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.3)', color: 'white' }}>←</button>
        <div>
          <h1 style={{ fontFamily: 'var(--font-title)', color: 'white', fontSize: '1.5rem' }}>🍳 Cooking</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>Fun recipes for kids!</p>
        </div>
      </div>
      <div className="screen-body" style={{ paddingTop: 20 }}>
        <p style={{ color: 'var(--gray)', fontSize: '0.85rem', marginBottom: 16 }}>⚠️ Always cook with a grown-up nearby!</p>
        {RECIPES.map(recipe => (
          <div key={recipe.id} className="card card-clickable" onClick={() => setSelectedRecipe(recipe.id)} style={{ display: 'flex', gap: 14, marginBottom: 12 }}>
            <div style={{ fontSize: '3rem', width: 60, textAlign: 'center' }}>{recipe.emoji}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, marginBottom: 4 }}>{recipe.title}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <span style={{ background: recipe.color + '22', color: recipe.color, padding: '2px 10px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 700 }}>⏱ {recipe.time}</span>
                <span style={{ background: '#E8F5E9', color: 'var(--green)', padding: '2px 10px', borderRadius: 10, fontSize: '0.75rem', fontWeight: 700 }}>{recipe.level}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
